import { useState } from 'react';
import { addDays, format } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import api from 'api';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Alert, Button, TargetSchedule } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Emails,
  Form,
  FormGroup,
  ReminderIntervalsSelector
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';

const SettingsSchedule = () => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: { timeZone }
  } = useReduxState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setIsSubmitting(true);

      const data = {
        ...values,
        deadlineType:
          values.deadlineType !== 'none' ? values.deadlineType : null,
        deadlineDate: values.deadlineDate
          ? toDate(`${format(values.deadlineDate, 'y-MM-dd')}T23:59:59`, {
              timeZone
            })
          : null
      };

      await api.put(`/admin/courses/${course.id}`, data);

      const courses = await api.get(`/admin/courses?id=${course.id}`);
      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const initialValues = {
    deadlineDate: course.deadlineDate
      ? Date.parse(toZonedTime(course.deadlineDate, timeZone).toString())
      : Date.parse(addDays(now, 1).toString()),
    deadlineDenyAccess: course.deadlineDenyAccess,
    deadlineDurationUnit: course.deadlineDurationUnit || 'days',
    deadlineDurationValue: course.deadlineDurationValue || 1,
    deadlineReminderIntervals: course.deadlineReminderIntervals,
    deadlineRemindersEmailList: course.deadlineRemindersEmailList || '[]',
    deadlineRemindersOthers: course.deadlineRemindersOthers || false,
    deadlineRemindersUser: course.deadlineRemindersUser || false,
    deadlineType: course.deadlineType || 'none',
    isMandatory: course.isMandatory
  };

  return (
    <div className="schedule">
      <Box title={messages.courseMandatoryAndTargetSchedule}>
        {!course.completionEnabled ? (
          <Alert
            style={{ marginBottom: 20 }}
            text={messages.courseTargetScheduleCompletionsDisabled}
            type="warning-light"
          />
        ) : null}

        <Form
          allowEmptyStrings={true}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <>
              <UnsavedFormChanges initialValues={initialValues} />

              <TargetSchedule
                settings={course}
                includeMandatoryOption={true}
                helpText={`${getMessageWithPlaceholders({
                  message: messages.courseTargetScheduleInfo,
                  placeholders: { pageName: messages.commonShare }
                })} ${messages.courseTargetScheduleInfoPath}`}
                includeSpecificDateOption={true}
              />

              <FormGroup
                label={messages.commonAccess}
                style={{ marginTop: 25 }}
              >
                <Checkbox
                  field="deadlineDenyAccess"
                  id="deadline-deny-access"
                  label={messages.courseTargetScheduleDeny}
                />
              </FormGroup>

              <FormGroup
                label={messages.pathsSettingsSendReminders}
                labelHelpText={`${getMessageWithPlaceholders({
                  message: messages.publishTargetScheduleRemaindersDisabled,
                  placeholders: { coursePage: messages.settingsCompletion }
                })}.`}
                style={{ marginTop: 25 }}
              >
                <Checkbox
                  disabled={!course.completionEnabled}
                  field="deadlineRemindersUser"
                  id="deadlineRemindersUser"
                  label={messages.commonSendEmailReminders}
                />

                <Checkbox
                  disabled={!course.completionEnabled}
                  field="deadlineRemindersOthers"
                  id="deadlineRemindersOthers"
                  label={messages.commonSendAdditionalEmailReminders}
                  style={{ marginTop: 10 }}
                />

                <Emails
                  disabled={
                    !course.completionEnabled ||
                    !formState.values.deadlineRemindersOthers
                  }
                  field="deadlineRemindersEmailList"
                  id="deadlineRemindersEmailList"
                  style={{ marginTop: 5, marginLeft: 20 }}
                />

                <ReminderIntervalsSelector
                  disabled={
                    !course.completionEnabled ||
                    (!formState.values.deadlineRemindersUser &&
                      !formState.values.deadlineRemindersOthers)
                  }
                  field="deadlineReminderIntervals"
                  type="deadline"
                />
              </FormGroup>

              <FormGroup style={{ marginTop: 25 }}>
                <Button
                  category="success"
                  disabled={formState.invalid}
                  loading={isSubmitting}
                  text={messages.commonSaveChanges}
                  type="submit"
                />
              </FormGroup>
            </>
          )}
        </Form>
      </Box>
    </div>
  );
};

export default SettingsSchedule;
